import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, FunnelIcon } from '@heroicons/react/24/outline';
import { PageHeader } from '@/components/layout';
import { Button, Input, Select, Card, Badge, Pagination } from '@/components/common';
import { useUsers } from '@/hooks/useUsers';
import { useDebouncedSearch } from '@/hooks/useDebouncedSearch';
import { useAuthStore } from '@/store';
import { formatDateTime, titleCase } from '@/utils';
import { USER_STATUS_VALUES, userStatusOf, type UserStatus, type UserWithStatus } from '@/services/users';
import {
  USER_STATUS_BADGE_VARIANT,
  USER_STATUS_LABELS,
  useSetUserStatus,
} from './useSetUserStatus';
import type { UserFilters, UserRole } from '@/types';

const allRoleOptions = [
  { value: 'super_admin', label: 'Super Admin' },
  { value: 'country_admin', label: 'Country Admin' },
  { value: 'broker_admin', label: 'Broker Admin' },
  { value: 'broker_employee', label: 'Broker Employee' },
  { value: 'posp', label: 'POSP' },
  { value: 'super_posp', label: 'Super POSP' },
  { value: 'insurer_admin', label: 'Insurer Admin' },
  { value: 'insurer_employee', label: 'Insurer Employee' },
];

/**
 * Board #180: the Role dropdown offered every role on the platform to every
 * viewer, including roles that role's own scope excludes -- picking one of
 * those returns an empty list with no explanation, which reads as "there are
 * none" rather than "you cannot see those".
 *
 * These lists MIRROR `UserViewSet.get_queryset` (apps/users/views.py); they do
 * not create the rule. The server is the gate -- a hand-typed `?role=posp` from
 * a country admin still comes back empty because the queryset says so, not
 * because this dropdown omits it.
 */
const roleFilterByViewer: Record<string, string[]> = {
  super_admin: ['country_admin'],
  country_admin: ['broker_admin', 'insurer_admin', 'insurer_employee'],
  broker_admin: ['broker_employee', 'posp', 'super_posp'],
};

/**
 * Board #180: "Can manage status: Active / Suspend / Block".
 *
 * This dropdown used to be `is_active` true/false, which cannot ask the
 * question the board is about — a suspended and a blocked account are both
 * `is_active=false`. It now filters on the server's `status` column.
 */
const statusOptions: { value: string; label: string }[] = [
  { value: '', label: 'All Status' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'blocked', label: 'Blocked' },
];

/** The row actions, per current state: never offer the state you are in. */
const statusActionsFor = (current: UserStatus | null): UserStatus[] => {
  if (current === null) return ['active', 'suspended', 'blocked'];
  return (['active', 'suspended', 'blocked'] as UserStatus[]).filter((s) => s !== current);
};

const statusActionLabel: Record<UserStatus, string> = {
  active: 'Activate',
  suspended: 'Suspend',
  blocked: 'Block',
};

/**
 * The state badge.
 *
 * When the server did not send a `status` (an older payload), this shows what
 * it DID send — Active/Inactive from `is_active` — rather than guessing
 * "Suspended" over what might be a blocked account. Rendering a state nobody
 * asserted is how a screen ends up lying about an account.
 */
function StatusBadge({ user }: { user: UserWithStatus }) {
  const status = userStatusOf(user);
  if (status === null) {
    return (
      <Badge variant={user.is_active ? 'success' : 'default'} size="sm">
        {user.is_active ? 'Active' : 'Inactive'}
      </Badge>
    );
  }
  return (
    <Badge variant={USER_STATUS_BADGE_VARIANT[status]} size="sm">
      {user.status_display || USER_STATUS_LABELS[status]}
    </Badge>
  );
}

export function UsersListPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuthStore();
  const isCountryAdmin = currentUser?.role === 'country_admin';
  // Country admins must see Add User just like super/broker admins (FIX 27).
  const canAddUser = ['super_admin', 'country_admin', 'broker_admin'].includes(currentUser?.role ?? '');
  // Board #180: "Broker Admin ... Can manage status: Active / Suspend / Block
  // for POSP, Employees". These buttons used to render for country admins only
  // (and only on broker_admin/broker_employee rows), so the role the board puts
  // in charge of employee and POSP status could not change either. The list is
  // already scoped by the server, so every row shown here is one this caller may
  // act on -- the only exclusion is the caller's own row, below.
  const canManageStatus = ['super_admin', 'country_admin', 'broker_admin'].includes(currentUser?.role ?? '');
  const [page, setPage] = useState(1);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<UserFilters & { status?: UserStatus }>({});
  const roleOptions = useMemo(() => {
    const allowed = roleFilterByViewer[currentUser?.role ?? ''];
    if (!allowed) return [{ value: '', label: 'All Roles' }, ...allRoleOptions];
    return [
      // A country admin's unfiltered list IS the broker list (the server
      // defaults it there), so "All Roles" would be a false label.
      { value: '', label: currentUser?.role === 'country_admin' ? 'Brokers' : 'All Roles' },
      ...allRoleOptions.filter((option) => allowed.includes(option.value)),
    ];
  }, [currentUser?.role]);
  // Global rule 1 -- search-as-you-type without a request per keystroke:
  // `searchTerm` drives the Input synchronously, `debouncedTerm` (300ms)
  // feeds `filters.search`, replacing the old submit-on-Enter form.
  const { term: searchTerm, debouncedTerm, setTerm: setSearchTerm } = useDebouncedSearch();

  const { data, isLoading } = useUsers(page, filters);
  const setStatus = useSetUserStatus();
  const users: UserWithStatus[] = data?.results ?? [];
  const totalItems = data?.count ?? 0;
  const itemsPerPage = 20;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  useEffect(() => {
    const next = debouncedTerm || undefined;
    setFilters((prev) => (prev.search === next ? prev : { ...prev, search: next }));
    setPage(1);
  }, [debouncedTerm]);

  const handleRoleChange = (value: string) => {
    setFilters((prev) => ({ ...prev, role: (value || undefined) as UserRole | undefined }));
    setPage(1);
  };

  const handleStatusChange = (value: string) => {
    // Narrowed against the three the server knows: a value outside them would
    // be sent as `?status=` and silently match nothing, which reads on screen
    // as "there are no such users".
    const next = USER_STATUS_VALUES.find((s) => s === value);
    setFilters((prev) => ({ ...prev, status: next }));
    setPage(1);
  };

  const handleRowClick = (user: UserWithStatus) => {
    navigate(`/users/${user.id}`);
  };

  const getRoleBadgeVariant = (role: string) => {
    if (role.includes('admin')) return 'error';
    if (role.includes('posp')) return 'warning';
    return 'info';
  };

  return (
    <div>
      <PageHeader
        title="User Management"
        subtitle={`${totalItems} users`}
        breadcrumbs={[{ label: 'Users' }]}
        actions={
          canAddUser ? (
            <Link to="/users/new">
              <Button leftIcon={<PlusIcon className="w-5 h-5" />}>Add User</Button>
            </Link>
          ) : undefined
        }
      />

      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <Input
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search by name, email, or phone..."
              leftIcon={<MagnifyingGlassIcon className="w-5 h-5" />}
            />
          </div>
          <Button
            variant="outline"
            onClick={() => setShowFilters(!showFilters)}
            leftIcon={<FunnelIcon className="w-5 h-5" />}
          >
            Filters
          </Button>
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4 pt-4 border-t border-gray-100">
            <Select
              options={roleOptions}
              value={filters.role || ''}
              onChange={(e) => handleRoleChange(e.target.value)}
              placeholder="Role"
            />
            <Select
              options={statusOptions}
              value={filters.status ?? ''}
              onChange={(e) => handleStatusChange(e.target.value)}
              placeholder="Status"
            />
          </div>
        )}
      </Card>

      <Card padding="none">
        {isLoading ? (
          <UserTableSkeleton />
        ) : users.length === 0 ? (
          <EmptyState />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      User
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Contact
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Role
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Last Login
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users.map((user) => (
                    <tr
                      key={user.id}
                      className={`hover:bg-gray-50 transition-colors ${!isCountryAdmin ? 'cursor-pointer' : ''}`}
                      onClick={() => !isCountryAdmin && handleRowClick(user)}
                    >
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
                            <span className="text-primary-600 font-medium text-sm">
                              {user.first_name?.[0] ?? ''}{user.last_name?.[0] ?? ''}
                            </span>
                          </div>
                          <div>
                            <p className="text-sm font-medium text-gray-900">{user.full_name}</p>
                            <p className="text-xs text-gray-500">{user.tenant_type ? titleCase(user.tenant_type) : 'Platform'}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-sm text-gray-900">{user.phone || '-'}</p>
                        {user.email && (
                          <p className="text-xs text-gray-500">{user.email}</p>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <Badge variant={getRoleBadgeVariant(user.role)} size="sm">
                          {titleCase(user.role)}
                        </Badge>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {user.last_login ? formatDateTime(user.last_login) : '-'}
                      </td>
                      <td className="px-6 py-4">
                        <StatusBadge user={user} />
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          {/* Queue #211: the `/users/:id/edit` route and an
                              edit-capable UserFormPage already existed; this
                              screen just offered no way in. A country admin
                              could not even open a user, because the row is
                              click-through only for other roles. */}
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/users/${user.id}/edit`);
                            }}
                          >
                            Edit
                          </Button>
                          {/* Board #180: Active / Suspend / Block, as three
                              buttons rather than one toggle, because the state
                              you move TO is the thing being chosen. The state
                              the row is already in is not offered.

                              Never on your own row: the endpoint answers 400
                              for a self-target, because suspending yourself
                              locks you out of this very screen. */}
                          {canManageStatus &&
                            user.id !== currentUser?.id &&
                            statusActionsFor(userStatusOf(user)).map((next) => (
                              <Button
                                key={next}
                                variant="outline"
                                size="sm"
                                disabled={setStatus.isPending}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setStatus.mutate({ id: user.id, status: next });
                                }}
                              >
                                {statusActionLabel[next]}
                              </Button>
                            ))}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                totalItems={totalItems}
                itemsPerPage={itemsPerPage}
                onPageChange={setPage}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
}

function UserTableSkeleton() {
  return (
    <div className="animate-pulse">
      <div className="h-12 bg-gray-100" />
      {[...Array(5)].map((_, i) => (
        <div key={i} className="h-16 border-t border-gray-100 flex items-center px-6 gap-4">
          <div className="w-10 h-10 bg-gray-200 rounded-full" />
          <div className="flex-1">
            <div className="h-4 bg-gray-200 rounded w-32 mb-2" />
            <div className="h-3 bg-gray-200 rounded w-20" />
          </div>
          <div className="h-4 bg-gray-200 rounded w-28" />
          <div className="h-6 bg-gray-200 rounded-full w-24" />
          <div className="h-4 bg-gray-200 rounded w-32" />
          <div className="h-6 bg-gray-200 rounded-full w-16" />
        </div>
      ))}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="text-center py-12">
      <div className="mx-auto h-12 w-12 text-gray-400">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
          />
        </svg>
      </div>
      <h3 className="mt-4 text-sm font-semibold text-gray-900">No users found</h3>
      <p className="mt-1 text-sm text-gray-500">Get started by adding a new user.</p>
      <div className="mt-6">
        <Link to="/users/new">
          <Button leftIcon={<PlusIcon className="w-5 h-5" />}>Add User</Button>
        </Link>
      </div>
    </div>
  );
}
